import { useMemo } from 'react';
import { AppointmentTypesTypes } from '@frontend/api-appointment-types';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Table, Text, TableColumnConfig, currency, TableRowActions } from '@frontend/design-system';
import { AppointmentTypeTableProps } from './types';

type Props = {
  appointmentTypesData: AppointmentTypesTypes.AppointmentType[];
  tableRowActions: TableRowActions<AppointmentTypesTypes.AppointmentType>['actions'];
} & AppointmentTypeTableProps;

export const AppointmentTypeLargeTable = ({
  appointmentTypesData,
  externalAppointmentTypes,
  showAmountColumn = false,
  isLoading = false,
  tableRowActions,
}: Props) => {
  const { t } = useTranslation('schedule');
  const hasExternalAppointmentTypes = !!externalAppointmentTypes?.length;

  const tableColumns = useMemo(
    (): TableColumnConfig<AppointmentTypesTypes.AppointmentType>[] => [
      {
        Header: t('Display Name'),
        id: 'displayName',
        accessor: (row) => row.name,
        disableSortBy: true,
        width: 250,
        minWidth: 200,
      },
      {
        Header: t('Imported Name'),
        id: 'externalAppointmentType',
        accessor: (row) => row.externalAppointmentTypeId,
        disableSortBy: true,
        width: 250,
        minWidth: 200,
        omit: !hasExternalAppointmentTypes,
        cellRenderer: (cellValue: string) => {
          const externalAppointmentTypesName = externalAppointmentTypes?.find(({ id }) => id === cellValue)?.name;
          if (externalAppointmentTypesName) return externalAppointmentTypesName;
          return (
            <>
              <Icon name='alert-invert' size={24} color='error' />
              <Text css={{ marginLeft: theme.spacing(1) }} size='small' color='light' as='span'>
                {t('Please select external appointment type')}
              </Text>
            </>
          );
        },
      },
      {
        Header: t('Duration'),
        id: 'durationMinutes',
        accessor: (row) => row.durationMinutes + t(' mins'),
        disableSortBy: true,
        width: 100,
        minWidth: 100,
        maxWidth: 200,
      },
      {
        Header: t('Payment Amount'),
        id: 'paymentAmount',
        omit: !showAmountColumn,
        disableSortBy: true,
        accessor: (row) =>
          row.hasGlobalBookingDeposit
            ? row.globalBookingDepositAmount
            : row.hasBookingDeposit
            ? row.bookingDepositAmount
            : 0,
        cellRenderer: (amount) => (amount ? currency(amount, { decimals: 2 }) : '-'),
        minWidth: 150,
        maxWidth: 150,
      },
      {
        Header: t('Providers'),
        id: 'practitioners',
        accessor: (row) => row.providerAssets,
        disableSortBy: true,
        minWidth: 300,
        maxWidth: 650,
        cellRenderer: (cellValue: AppointmentTypesTypes.ProviderAsset[]) => {
          return cellValue
            ?.filter((provider) => provider.available)
            .map((provider) => provider.providerName)
            .join(', ');
        },
      },
    ],
    [externalAppointmentTypes, showAmountColumn]
  );

  return (
    <Table
      isLoading={isLoading}
      colConfig={tableColumns}
      tableStyle={{
        name: 'appt-type-table',
        styles: `margin-bottom: ${theme.spacing(4)};`,
      }}
      data={appointmentTypesData}
      hasResponsiveColWidths
      rowActions={{
        actions: tableRowActions,
      }}
    />
  );
};
